import { useState } from "react";
import ClubBadge from "../../ClubBadge";
import { getClubBadge } from "../../../data/clubBadges";
import { Club } from "../../../api/clubs";
import { inputStyle } from "../../../styles";

interface Props {
  clubs: Club[];
  selectedClubIds: string[];
  onChange: (ids: string[]) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noResultsMessage?: string;
  clearSelectionLabel?: string;
  maxHeight?: string;
}

export default function ClubMultiSelect({
  clubs, selectedClubIds, onChange, searchPlaceholder, emptyMessage, noResultsMessage, clearSelectionLabel, maxHeight = "180px",
}: Props) {
  const [search, setSearch] = useState("");

  const filtered = clubs.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) || (c.shortName ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    onChange(selectedClubIds.includes(id) ? selectedClubIds.filter((x) => x !== id) : [...selectedClubIds, id]);
  };

  return (
    <div>
      <input
        type="text"
        placeholder={searchPlaceholder ?? "Search clubs..."}
        style={{ ...inputStyle, marginBottom: "0.4rem" }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div style={{ maxHeight, overflowY: "auto", border: "1px solid var(--color-border)", borderRadius: "10px", padding: "0.25rem 0" }}>
        {clubs.length === 0 ? (
          <p style={{ padding: "1rem", textAlign: "center", color: "var(--color-text-muted)", margin: 0, fontSize: "0.85rem" }}>
            {emptyMessage ?? "No clubs available"}
          </p>
        ) : filtered.length === 0 ? (
          <p style={{ padding: "1rem", textAlign: "center", color: "var(--color-text-muted)", margin: 0, fontSize: "0.85rem" }}>
            {noResultsMessage ?? "No matches"}
          </p>
        ) : (
          filtered.map((club) => {
            const checked = selectedClubIds.includes(club.id);
            return (
              <label key={club.id} style={{
                display: "flex", alignItems: "center", gap: "0.5rem",
                padding: "0.35rem 0.75rem", cursor: "pointer",
                background: checked ? "rgba(124,111,255,0.07)" : "transparent",
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(club.id)} style={{ flexShrink: 0 }} />
                <ClubBadge config={getClubBadge(club.name)} size={18} title={club.name} />
                <span style={{ fontSize: "0.85rem", color: "var(--color-text)", flex: 1 }}>{club.name}</span>
                {club.shortName && club.shortName !== club.name && (
                  <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>{club.shortName}</span>
                )}
              </label>
            );
          })
        )}
      </div>
      {selectedClubIds.length > 0 && (
        <button
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", color: "var(--color-text-muted)", padding: "0.25rem 0", marginTop: "0.25rem" }}
          onClick={() => onChange([])}
        >
          {clearSelectionLabel ?? "Clear selection"}
        </button>
      )}
    </div>
  );
}
